import React, { useState, useRef, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CaretDown, Check, MagnifyingGlass, X } from "@phosphor-icons/react";
import { cn } from "./cn";

export default function Select({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  label,
  icon: Icon,
  searchable = false,
  clearable = false,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = options.find((o) => o.value === value) || null;
  const filtered = useMemo(
    () => (searchable && q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options),
    [options, q, searchable]
  );

  return (
    <div className={cn("w-full", className)} ref={ref}>
      {label && <label className="mb-1.5 block text-start text-sm font-medium text-ink/90">{label}</label>}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-line bg-surface-2 px-3.5 text-sm outline-none transition",
            "focus:border-accent focus:ring-2 focus:ring-accent/30",
            open && "border-accent ring-2 ring-accent/30",
            disabled && "opacity-60"
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {Icon && <Icon size={17} className="shrink-0 text-faint" />}
            <span className={cn("truncate text-start", selected ? "text-ink" : "text-faint")}>
              {selected ? selected.label : placeholder}
            </span>
          </span>
          <span className="flex items-center gap-1">
            {clearable && selected && (
              <X
                size={15}
                className="text-faint hover:text-ink"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(null);
                }}
              />
            )}
            <CaretDown size={16} className={cn("shrink-0 text-faint transition-transform", open && "rotate-180")} />
          </span>
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
            >
              {searchable && (
                <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                  <MagnifyingGlass size={16} className="text-faint" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search…"
                    className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-faint"
                  />
                </div>
              )}
              <ul className="max-h-60 overflow-auto p-1">
                {filtered.length === 0 && <li className="px-3 py-2 text-sm text-faint">No results</li>}
                {filtered.map((o) => (
                  <li key={o.value}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange?.(o.value);
                        setOpen(false);
                        setQ("");
                      }}
                      className={cn(
                        "flex w-full items-center justify-between rounded-lg px-3 py-2 text-start text-sm transition",
                        o.value === value ? "bg-accent-soft text-accent-strong" : "text-ink hover:bg-surface-2"
                      )}
                    >
                      <span className="truncate">{o.label}</span>
                      {o.value === value && <Check size={16} weight="bold" className="text-accent" />}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
